import type { ApolloClient } from '@apollo/client';
import { from, map } from 'rxjs';
import {
    MeasurementSettingsRuntimeDataDocument,
    type MeasurementSettingsRuntimeDataQuery,
    type MeasurementSettingsRuntimeDataQueryVariables,
} from '@/graphql/types';
import type { RuntimeDataServiceInput, RuntimeDataServiceOutput } from '../contracts';
import { buildRequiredMeasuringPointWindowInput } from '../measuringPointInput';
import {
    RuntimeDataService,
    type RuntimeDataServiceRequest,
    type RuntimeResultLinkDescriptorTabular,
    type RuntimeTabularColumnDescriptor,
} from '../types';
import {
    isRecord,
    normalizeArrayEntries,
    normalizeOrderedTimeWindow,
    normalizeRequiredPositiveIntegerId,
    normalizeString,
    toPositiveIntegerIdNumber,
} from '../validationPrimitives';

type MeasurementSettingEntry =
    RuntimeDataServiceOutput<'measuring_point.measurement_settings'>['entries'][number];

function validateMeasurementSettingsInput(
    args: unknown
): RuntimeDataServiceInput<'measuring_point.measurement_settings'> {
    if (!isRecord(args)) {
        throw new Error('Expected "measuring_point.measurement_settings" input to be an object.');
    }

    const { startTime, endTime } = normalizeOrderedTimeWindow(
        args.startTime,
        args.endTime,
        'measuring_point.measurement_settings'
    );

    return {
        measuringPointId: normalizeRequiredPositiveIntegerId(
            args.measuringPointId,
            'measuring_point.measurement_settings.measuringPointId'
        ),
        startTime,
        endTime,
    };
}

function normalizeMeasurementSettingEntry(
    value: unknown,
    pathLabel: string
): MeasurementSettingEntry {
    if (!isRecord(value)) {
        throw new Error(`Expected "${pathLabel}" to be an object.`);
    }

    return {
        // Both are display text the backend already translated, like the comments table's
        // rows. Whether either is empty is its business, not ours to fail a report over.
        name: normalizeString(value.name, `${pathLabel}.name`),
        value: normalizeString(value.value, `${pathLabel}.value`),
    };
}

function validateMeasurementSettingsResult(
    result: unknown
): RuntimeDataServiceOutput<'measuring_point.measurement_settings'> {
    if (!isRecord(result)) {
        throw new Error('Expected "measuring_point.measurement_settings" result to be an object.');
    }

    return {
        entries: normalizeArrayEntries(
            result.entries,
            'measuring_point.measurement_settings.entries',
            normalizeMeasurementSettingEntry
        ),
    };
}

function parseMeasurementSettingsTableRows(value: unknown): readonly MeasurementSettingEntry[] {
    return validateMeasurementSettingsResult({ entries: value }).entries;
}

function createMeasurementSettingColumns() {
    return [
        {
            kind: 'text',
            key: 'name',
            label: gettext('NAME'),
            defaultAlignment: 'left',
            resolveCell: (entry: MeasurementSettingEntry) => entry.name,
        },
        {
            kind: 'text',
            key: 'value',
            label: gettext('VALUE'),
            defaultAlignment: 'left',
            resolveCell: (entry: MeasurementSettingEntry) => entry.value,
        },
    ] as const satisfies readonly RuntimeTabularColumnDescriptor<MeasurementSettingEntry>[];
}

export class MeasurementSettingsRuntimeDataService extends RuntimeDataService<'measuring_point.measurement_settings'> {
    static readonly serviceId = 'measuring_point.measurement_settings' as const;

    static get serviceMetadata() {
        return {
            label: gettext('DATA_SERVICE_MEASUREMENT_SETTINGS_LABEL'),
            description: gettext('DATA_SERVICE_MEASUREMENT_SETTINGS_DESCRIPTION'),
        } as const;
    }

    static get serviceResultLinks() {
        return [
            {
                key: 'entries',
                label: gettext('DATA_SERVICE_MEASUREMENT_SETTINGS_ENTRIES_LABEL'),
                description: gettext('DATA_SERVICE_MEASUREMENT_SETTINGS_ENTRIES_DESCRIPTION'),
                supportedContextKinds: ['measuring-point'],
                // The backend derives every row from the config field name alone, so this
                // describes a sound or air measuring point as readily as a vibration one.
                supportedSwarmTypes: null,
                path: 'entries',
                valueKind: 'tabular',
                argPolicy: 'forbidden',
                exposeInInsertionPresets: true,
                columns: createMeasurementSettingColumns(),
                parseTableRows: parseMeasurementSettingsTableRows,
                buildInput: (args) =>
                    buildRequiredMeasuringPointWindowInput(args, 'measurement settings'),
            },
        ] as const satisfies readonly RuntimeResultLinkDescriptorTabular<
            'measuring_point.measurement_settings',
            MeasurementSettingEntry
        >[];
    }

    constructor(private readonly client: ApolloClient<object>) {
        super();
    }

    validateArgs = validateMeasurementSettingsInput;

    validateResult = validateMeasurementSettingsResult;

    createResult$ = ({
        input,
    }: RuntimeDataServiceRequest<'measuring_point.measurement_settings'>) =>
        from(
            this.client.query<
                MeasurementSettingsRuntimeDataQuery,
                MeasurementSettingsRuntimeDataQueryVariables
            >({
                query: MeasurementSettingsRuntimeDataDocument,
                variables: {
                    measuringPointId: toPositiveIntegerIdNumber(
                        input.measuringPointId,
                        'measuring_point.measurement_settings.measuringPointId'
                    ),
                    startTime: input.startTime,
                    endTime: input.endTime,
                },
                fetchPolicy: 'cache-first',
            })
        ).pipe(
            map(({ data }) =>
                validateMeasurementSettingsResult({ entries: data.measurementSettings })
            )
        );
}

export function createMeasurementSettingsService(
    client: ApolloClient<object>
): MeasurementSettingsRuntimeDataService {
    return new MeasurementSettingsRuntimeDataService(client);
}
